import React, { useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import {
  PostMediaEditing,
  PostVideoEditing,
} from "features/post-media-file-editing";

import { ConfirmRequired } from "ui/confirm-required";
import { cn } from "@/lib/utils";
import type { PostAssetProps } from "./post-selected-assets";
import type { PostMediaFile } from "shared/types/post";

export type PostSelectedAssetView = "list" | "compressed" | "grid";

interface PostMediaEditingProps {
  onEdit(asset: PostAssetProps): void;
}

type DraggableHtmlProps = React.HTMLAttributes<HTMLDivElement> &
  Partial<Record<`data-${string}`, string>>;

interface PostSelectedAssetProps {
  asset: PostAssetProps;
  className?: string;
  onEdit(file: PostMediaFile): void;
  onRemove(): void;
  isDragging?: boolean;
  draggableProps: {
    innerRef: React.Ref<HTMLDivElement>;
    dragHandleProps: DraggableHtmlProps;
    draggableProps: DraggableHtmlProps;
  };
  disabled?: boolean;
  view?: PostSelectedAssetView;
}

export const PostSelectedAsset = (props: PostSelectedAssetProps) => {
  const {
    asset,
    className = "",
    onEdit,
    onRemove,
    isDragging = false,
    draggableProps,
    disabled,
    view = "grid",
  } = props;

  const [actionsVisible, setActionsVisible] = useState(false);
  const isDocument = asset.type === "document";
  const isVideo = asset.isVideo || (asset.type && asset.type.includes("video"));
  const isImage =
    ((asset.type && !asset.type.includes("video")) || !asset.isVideo) &&
    !isDocument;
  const dragHandleProps = disabled ? {} : draggableProps.dragHandleProps;
  const filename = asset.fileName || asset.name || "Post media";
  const dimensions =
    asset.meta?.width && asset.meta?.height
      ? `${asset.meta.width}x${asset.meta.height}`
      : undefined;
  const previewUrl =
    asset.thumbnailUrl ||
    asset.gifThumbnail ||
    asset.downloadUrl ||
    asset.url ||
    asset.originalUrl ||
    "";
  const mediaIcon = isDocument
    ? "fa-file-pdf"
    : isVideo
      ? "fa-video"
      : "fa-image";

  const renderEditButton = () => {
    if (disabled) {
      return null;
    }

    if (isImage) {
      return (
        <PostMediaEditing onChange={onEdit}>
          {({ onEdit }: PostMediaEditingProps) => {
            const isImageEditable = !["heif", "heic"].includes(
              asset?.meta?.format?.type || ""
            );

            return (
              <button
                type="button"
                aria-label="Edit media"
                className={actionButtonClassName}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (isImageEditable) {
                    onEdit(asset);
                  }
                }}
                disabled={!isImageEditable}
              >
                <i className="fa-regular fa-pen-to-square" aria-hidden />
              </button>
            );
          }}
        </PostMediaEditing>
      );
    }

    if (isVideo) {
      return (
        <PostVideoEditing onChange={onEdit}>
          {({ onEdit }: PostMediaEditingProps) => (
            <button
              type="button"
              aria-label="Edit media"
              className={actionButtonClassName}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onEdit(asset);
              }}
            >
              <i className="fa-regular fa-pen-to-square" aria-hidden />
            </button>
          )}
        </PostVideoEditing>
      );
    }

    return null;
  };

  const renderRemoveButton = () =>
    disabled ? null : (
      <ConfirmRequired
        dialogClassName="confirm-remove-post-media"
        title="Remove Post Media"
        onConfirm={onRemove}
        confirmText="Remove"
        cancelText="Cancel"
        message="Are you sure you want to remove this media from the post?"
      >
        {({ onClick }) => (
          <button
            type="button"
            aria-label="Remove media"
            className={cn(actionButtonClassName, "text-[#c83f2d]")}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onClick();
            }}
          >
            <i className="fa-regular fa-trash-can" aria-hidden />
          </button>
        )}
      </ConfirmRequired>
    );

  const actions = (
    <div
      className={cn(
        "post-selected-asset-actions flex items-center justify-end",
        view === "list"
          ? "static w-14 flex-[0_0_56px]"
          : view === "compressed"
            ? "absolute left-1/2 top-1/2 z-[2] -translate-x-1/2 -translate-y-1/2 gap-1 rounded-lg bg-white/[0.96] p-1 transition-opacity duration-150"
            : "absolute right-2 top-2 z-[2] gap-2 rounded-lg bg-white/[0.96] p-1 transition-opacity duration-150",
        view !== "list" && (actionsVisible ? "opacity-100" : "opacity-0")
      )}
    >
      {renderEditButton()}
      {renderRemoveButton()}
    </div>
  );

  return (
    <AssetContainer
      className={cn("group/asset", className)}
      ref={draggableProps.innerRef}
      {...draggableProps.draggableProps}
      $disabled={disabled ?? false}
      $isDragging={isDragging ?? false}
      $view={view}
      data-testid={`selected-asset-${view}`}
      onMouseEnter={() => {
        if (!disabled && view !== "list") setActionsVisible(true);
      }}
      onMouseLeave={() => setActionsVisible(false)}
      onFocusCapture={() => {
        if (!disabled && view !== "list") setActionsVisible(true);
      }}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setActionsVisible(false);
        }
      }}
    >
      <ConfirmRequiredStyled />
      {view === "list" && (
        <>
          <div
            {...dragHandleProps}
            aria-label={disabled ? undefined : "Drag media to reorder"}
            className="flex min-w-0 flex-1 cursor-grab items-center gap-2 active:cursor-grabbing"
          >
            <div className={mediaPreviewClassName("list")}>
              {previewUrl ? (
                <img
                  className="size-full object-cover"
                  src={previewUrl}
                  alt=""
                />
              ) : null}
            </div>
            <span className={mediaTypeIconClassName}>
              <i className={`fa-regular ${mediaIcon}`} aria-hidden />
            </span>
            <span className={filenameClassName} title={filename}>
              {filename}
            </span>
          </div>
          <span className="flex-none font-inter text-sm font-normal leading-5 text-[#808080]">
            {dimensions}
          </span>
          {actions}
        </>
      )}
      {view === "compressed" && (
        <div
          {...dragHandleProps}
          aria-label={disabled ? undefined : "Drag media to reorder"}
          className="group relative size-[60px] cursor-grab active:cursor-grabbing"
        >
          <div className={mediaPreviewClassName("compressed")}>
            {previewUrl ? (
              <img className="size-full object-cover" src={previewUrl} alt="" />
            ) : null}
          </div>
          {actions}
        </div>
      )}
      {view === "grid" && (
        <div
          {...dragHandleProps}
          aria-label={disabled ? undefined : "Drag media to reorder"}
          className="group relative min-w-0 cursor-grab active:cursor-grabbing"
        >
          <div className={mediaPreviewClassName("grid")}>
            {previewUrl ? (
              <img className="size-full object-cover" src={previewUrl} alt="" />
            ) : null}
          </div>
          <div className="flex h-9 min-w-0 items-center gap-2 rounded-b-lg border border-t-0 border-solid border-[#e6e6e6] p-2">
            <span className={mediaTypeIconClassName}>
              <i className={`fa-regular ${mediaIcon}`} aria-hidden />
            </span>
            <span
              className={cn(filenameClassName, "text-xs leading-4")}
              title={filename}
            >
              {filename}
            </span>
          </div>
          {actions}
        </div>
      )}
    </AssetContainer>
  );
};

const ConfirmRequiredStyled = createGlobalStyle`
  .confirm-remove-post-media {
    max-width: 500px;
  }
`;

const actionButtonClassName =
  "flex size-6 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent p-1 text-[#333] hover:bg-[#f9f9f9] [&_i]:inline-flex [&_i]:size-[15px] [&_i]:items-center [&_i]:justify-center [&_i]:text-xs [&_i]:leading-[15px]";

const mediaPreviewClassName = (view: PostSelectedAssetView) =>
  cn(
    "relative shrink-0 overflow-hidden border border-solid border-[#e6e6e6] bg-[#f4f4f6]",
    view === "grid"
      ? "aspect-square h-auto w-full rounded-t-lg"
      : view === "compressed"
        ? "size-[60px] rounded-lg"
        : "size-10 rounded-lg"
  );

const mediaTypeIconClassName =
  "inline-flex size-5 flex-[0_0_20px] items-center justify-center text-[#333] [&_i]:text-[16px] [&_i]:leading-5";

const filenameClassName =
  "min-w-0 flex-auto truncate font-inter text-sm font-normal leading-5 text-[#333]";

const AssetContainer = styled.div<{
  $disabled: boolean;
  $isDragging: boolean;
  $view: PostSelectedAssetView;
}>`
  display: flex;
  position: relative;
  z-index: 0;
  min-width: 0;
  width: ${({ $view }) => ($view === "compressed" ? "60px" : "100%")};
  height: ${({ $view }) => ($view === "compressed" ? "60px" : $view === "list" ? "72px" : "auto")};
  align-items: center;
  gap: ${({ $view }) => ($view === "list" ? "16px" : "0")};
  border: 0;
  border-radius: ${({ $view }) => ($view === "list" ? "0" : "8px")};
  box-sizing: border-box;
  padding: ${({ $view }) => ($view === "list" ? "16px" : "0")};
  opacity: ${({ $isDragging }) => ($isDragging ? 0.7 : 1)};

  &:hover,
  &:focus-within {
    z-index: ${({ $disabled, $isDragging }) =>
      !$disabled && !$isDragging ? 10 : 0};
  }
`;
